/**
 * Shared security helpers used across the app to reject or sanitize
 * untrusted inputs before they reach risky sinks (URL navigation,
 * object spreads, user-facing error messages).
 *
 * Keep this file small and dependency-free — it should be importable
 * anywhere, including from code that runs before the app has mounted.
 */

/**
 * Returns true only for URLs that can be safely navigated to via
 * window.open / anchor href. Rejects javascript:, data:, vbscript:,
 * about:, file:, and malformed URLs. Use for any dynamic URL that
 * originated from Supabase rows, AI output, or third-party APIs.
 *
 * Relative URLs are rejected too — callers should pass absolute ones
 * (we don't try to resolve relative-to-origin here because most of
 * our dynamic sources are external links anyway).
 */
export function isSafeUrl(url: unknown): url is string {
  if (typeof url !== 'string' || url.trim().length === 0) return false;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
    if (!parsed.hostname) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Opens a URL in a new tab only if it passes isSafeUrl. Silently
 * no-ops on unsafe input so callers don't need to add a conditional
 * at every sink. Adds noopener,noreferrer to block window.opener
 * access from the new tab.
 */
export function openSafeUrl(url: unknown): void {
  if (isSafeUrl(url)) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Checks that a URL's hostname is in an explicit allowlist. Use for
 * OAuth redirect URLs returned by edge functions — even if the URL
 * parses validly, it should only point at the known IdP.
 */
export function isAllowedHost(url: unknown, allowedHosts: readonly string[]): url is string {
  if (!isSafeUrl(url)) return false;
  try {
    const host = new URL(url).hostname.toLowerCase();
    return allowedHosts.some((h) => {
      const allowed = h.toLowerCase();
      return host === allowed || host.endsWith('.' + allowed);
    });
  } catch {
    return false;
  }
}

/**
 * Safely parse JSON from untrusted storage (localStorage / sessionStorage)
 * and extract only the fields you explicitly name, using a per-field
 * coercer. Any other keys on the source (including __proto__ and
 * constructor) are discarded. Returns null if the input isn't valid
 * JSON or isn't an object.
 *
 * Example:
 *   const data = pickSafeFields(
 *     sessionStorage.getItem('pendingDeal'),
 *     {
 *       title: (v) => (typeof v === 'string' ? v.slice(0, 200) : ''),
 *       amount: (v) => (typeof v === 'number' && v >= 0 ? v : 0),
 *     }
 *   );
 */
export function pickSafeFields<T extends Record<string, unknown>>(
  jsonString: string | null | undefined,
  shape: { [K in keyof T]: (raw: unknown) => T[K] },
): T | null {
  if (!jsonString) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonString);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  const obj = parsed as Record<string, unknown>;
  const out = {} as T;
  for (const key of Object.keys(shape) as Array<keyof T>) {
    // Bracket-access only the known keys — this intentionally ignores
    // __proto__, constructor, and anything else not listed in `shape`.
    out[key] = shape[key](Object.prototype.hasOwnProperty.call(obj, key) ? obj[key as string] : undefined);
  }
  return out;
}

/**
 * Turn a Supabase / Stripe / generic error into a user-safe message
 * for display in toasts or form errors. Logs the full error internally
 * for debugging. Never returns raw DB error strings (which can leak
 * column names, table names, constraint definitions, etc.).
 *
 * Extend the known-code map as new user-visible surfaces appear.
 */
const KNOWN_ERROR_MESSAGES: Record<string, string> = {
  // Postgres error codes
  '23505': 'That record already exists.',
  '23503': "That action would break a related record — please remove the dependency first.",
  '23502': 'A required field is missing.',
  '42501': "You don't have permission to perform this action.",
  // Supabase auth
  'invalid_grant': 'Your session has expired. Please sign in again.',
  'user_already_exists': 'An account with this email already exists.',
  'email_not_confirmed': 'Please confirm your email before signing in.',
  'weak_password': 'Please choose a stronger password.',
};

/**
 * Sentinel prefixes for messages that our own database triggers /
 * RAISE EXCEPTION calls produce. These are written specifically to be
 * shown to end users (e.g. "Insufficient wallet balance for this deal..."),
 * so we pass them through verbatim rather than swap for a generic mapping.
 *
 * Add new entries when you write a new RAISE EXCEPTION you want surfaced
 * in toasts. Keep these short and unambiguous so message detection stays
 * robust.
 */
const USER_SAFE_ERROR_PREFIXES = [
  'Insufficient wallet balance',
  'Insufficient balance',
  'Funder wallet not found',
  'Cannot create deal:',
  'Task must be completed',
  'Task has already been paid',
  'Task has no payment amount',
];

export function mapSupabaseError(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  // Always log the full error for developer debugging
  // eslint-disable-next-line no-console
  console.error('[mapSupabaseError]', err);

  if (!err) return fallback;

  // Supabase returns { message, code, details, hint } — prefer code.
  const e = err as { code?: string; message?: string; error?: string };

  // Pass through messages our own database logic produced for end users.
  // These start with a known sentinel prefix and are already user-safe.
  if (e.message) {
    for (const prefix of USER_SAFE_ERROR_PREFIXES) {
      if (e.message.includes(prefix)) {
        // Strip any noisy "ERROR:  P0001: " or trailing "\nCONTEXT:..." that
        // PostgREST wraps around the original RAISE message.
        const m = e.message.match(/(?:ERROR:\s+P0001:\s+)?([\s\S]*?)(?:\s*CONTEXT:|$)/);
        return (m ? m[1] : e.message).trim();
      }
    }
  }

  if (e.code && KNOWN_ERROR_MESSAGES[e.code]) return KNOWN_ERROR_MESSAGES[e.code];

  // Supabase auth errors use `error` as a string code
  if (typeof e.error === 'string' && KNOWN_ERROR_MESSAGES[e.error]) {
    return KNOWN_ERROR_MESSAGES[e.error];
  }

  // Check for Postgres code embedded in message like "duplicate key value violates unique constraint (23505)"
  if (e.message) {
    for (const code of Object.keys(KNOWN_ERROR_MESSAGES)) {
      if (e.message.includes(code)) return KNOWN_ERROR_MESSAGES[code];
    }
  }

  return fallback;
}

/**
 * Mask an email for log output. Preserves first two chars of local
 * part and the top-level domain so engineers can still distinguish
 * records, without storing the full PII.
 *
 *   redactEmail('alice@example.com')  -> 'al***@***.com'
 *   redactEmail('bob@sub.co.uk')      -> 'bo***@***.uk'
 */
export function redactEmail(email: unknown): string {
  if (typeof email !== 'string' || !email.includes('@')) return '[redacted]';
  const [local, domain] = email.split('@');
  const head = local.slice(0, 2);
  const parts = domain.split('.');
  const tld = parts[parts.length - 1] || '';
  return `${head}***@***.${tld}`;
}
